/**
 * LogActivityHost.tsx — the ONE Log Activity sheet for the sales pages (Log
 * Activity slice C1, RULED 2026-10-08, R1). Dashboard mounts it around the
 * sales pages only. It owns the single sheet instance and the create handler
 * — create, then the complete that follows a logged call / email / meeting /
 * note, the toasts and the error behaviour exactly as slice B had them on the
 * Activities page — and exposes open(prefill, opener) plus a "created"
 * subscription. No window event.
 *
 * The L shortcut (slice C2, RULED 2026-10-09): ONE keydown listener on
 * document, bubble phase, never capture (Q9), registered for the host's whole
 * life — so a Sales page carries exactly one, and a page outside Sales none
 * (the boundary below, Q7). It opens the PLAIN sheet (Q6: no contact, no
 * deal, the last type used), with whatever had focus as the opener. The
 * order of the checks is the ruling: the switch first (Q1/Q2 — OFF means the
 * host does nothing at all and never asks the guard), then the host's own
 * open sheet, then keyboardGuard (bare key by `key`, no modifier at all,
 * not typing, not spent, nothing modal — which now includes the contact
 * drawer, Q4). The switch is device-local, per tenant and user (Q2).
 *
 * Focus (R3, R5): the opener names WHERE focus goes when the sheet closes,
 * resolved at close time — the contact's row after the drawer closed, the
 * card's kebab after its menu item unmounted. When the resolved element is
 * gone, focus falls back to the page's main region (made focusable for that
 * one call), never to whatever the browser picks.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { SalesContact } from '../../types'
import type { SalesActivity as WireActivity } from '../../../../api/types'
import { fetchContacts, createActivity, completeActivity, cachedMeIdentity } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import type { CreateActivityBody } from './activityLog'
import { LogActivitySheet, MARK_DONE_FAILED } from './LogActivitySheet'
import { LogActivityContext, type LogActivityApi, type LogActivityPrefill, type FocusReturn, LOG_ACTIVITY_SHORTCUT } from './logActivityShared'
import { shortcutEligible } from '../../../../lib/keyboardGuard'
import { shortcutsPrefKey, readShortcutsEnabled } from '../../../../lib/shortcutsPref'
import { refreshSalesFigures } from '../../salesFigures'
import { useWriteAccess } from '../../../../lib/writeAccess'

/** The contacts read's own cap — the picker's list AND the Activities rows'
 *  name lookup read the same page (OwnerSelect's DIRECTORY_CAP shape). */
export const CONTACTS_CAP = 200

/** The deterministic fallback: the page's main region. */
function focusFallback() {
  const main = document.querySelector('main')
  if (!(main instanceof HTMLElement)) return
  if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1')
  main.focus()
}

interface Session {
  key: number
  prefill: LogActivityPrefill
  contacts: SalesContact[] | Promise<SalesContact[]>
}

/** The boundary Dashboard wraps every page in (R1, Q7): a Sales page gets
 *  the host — and with it the one L listener — any other page gets nothing. */
export function LogActivityBoundary({ activePage, children }: { activePage: string; children: React.ReactNode }) {
  return activePage.startsWith('sales.') ? <LogActivityHost>{children}</LogActivityHost> : <>{children}</>
}

export function LogActivityHost({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  // The listener reads this, not the state: it is registered once.
  const sessionOpen = useRef(false)
  const focusReturn = useRef<FocusReturn | null>(null)
  const [closedCount, setClosedCount] = useState(0)
  const listeners = useRef(new Set<(created: WireActivity) => void>())
  const nextKey = useRef(0)
  // A role that cannot create activities gets no L shortcut (and, through
  // useLogActivity, no opener): records access, lib/writeAccess.
  const { records } = useWriteAccess()

  const open = useCallback((prefill: LogActivityPrefill, back: FocusReturn) => {
    focusReturn.current = back
    // A failed read leaves the picker empty; the pre-filled contact still shows.
    const contacts = prefill.contacts ?? fetchContacts({ limit: CONTACTS_CAP }).catch(() => [] as SalesContact[])
    nextKey.current += 1
    sessionOpen.current = true
    setSession({ key: nextKey.current, prefill, contacts })
  }, [])

  // The L shortcut (C2). One listener, document, bubble phase, for the
  // host's life; removed with it.
  useEffect(() => {
    if (!records) return
    const onKeyDown = (e: KeyboardEvent) => {
      // Q1/Q2: OFF is nothing at all — the guard is not even asked.
      if (!readShortcutsEnabled(shortcutsPrefKey(cachedMeIdentity()))) return
      if (sessionOpen.current) return
      // The key, named POSITIVELY in the listener's own source (`.key ===`,
      // which survives minification as written) so the affordance inventory,
      // which reads a listener's `.key ===` literals, can name this one; the
      // guard repeats the compare, lower-cased.
      if (e.key === 'l' || e.key === 'L') {
        if (!shortcutEligible(e, LOG_ACTIVITY_SHORTCUT)) return
        e.preventDefault()
        // The opener is whatever had focus; the body is nobody, so the host's
        // fallback (the page's main region) takes that case.
        const active = document.activeElement
        const opener = active instanceof HTMLElement && active !== document.body ? active : null
        open({}, () => opener)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, records])

  const subscribe = useCallback((fn: (created: WireActivity) => void) => {
    listeners.current.add(fn)
    return () => { listeners.current.delete(fn) }
  }, [])

  const [api] = useState<LogActivityApi>(() => ({ open, subscribe }))

  const close = useCallback(() => {
    sessionOpen.current = false
    setSession(null)
    setClosedCount(n => n + 1)
  }, [])

  // After the sheet has unmounted (its focus trap has already tried its own
  // opener), send focus where the opener asked. Runs once per close.
  useEffect(() => {
    if (closedCount === 0) return
    const back = focusReturn.current
    focusReturn.current = null
    const el = back ? back() : null
    if (el && el.isConnected) el.focus()
    else focusFallback()
  }, [closedCount])

  const emit = (created: WireActivity) => { for (const fn of listeners.current) fn(created) }

  // Slice B's handler, moved verbatim in behaviour: the activity EXISTS once
  // the create answers 2xx; a failed complete leaves it pending beside a real
  // error; a refused create keeps the sheet open with the rep's input.
  async function save(body: CreateActivityBody, markDone: boolean, typeLabel: string): Promise<boolean> {
    let created: WireActivity
    try {
      created = await createActivity(body)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to log activity'))
      return false
    }
    if (markDone) {
      try {
        created = await completeActivity(created.id)
      } catch (e) {
        emit(created)
        void refreshSalesFigures()   // the activity exists (pending) even though the complete failed
        toast.error(apiErrorMessage(e, MARK_DONE_FAILED))
        return true
      }
    }
    emit(created)
    // The sidebar's Activities count reads the new row (ruled 2026-10-12).
    void refreshSalesFigures()
    toast.success(`${typeLabel} logged successfully`)
    return true
  }

  return (
    <LogActivityContext.Provider value={api}>
      {children}
      {session && records && (
        <LogActivitySheet key={session.key}
          onClose={close}
          onSave={save}
          contacts={session.contacts}
          initialContact={session.prefill.contact ?? null}
          initialDeal={session.prefill.deal ?? null}
          initialType={session.prefill.type ?? null}
        />
      )}
    </LogActivityContext.Provider>
  )
}
